// src/components/common/Layout/EasyCheckAppBar.js
import { useState } from 'react';
import {
  AppBar as MuiAppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Badge
} from '@mui/material';
import {
  Menu as MenuIcon,
  Logout,
  Settings,
  Person,
  NotificationsOutlined
} from '@mui/icons-material';
import PropTypes from 'prop-types';
import { useEasyCheck } from '../../../context/EasyCheckContext';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';

// Logo styled components
const LogoText = styled(Typography)(({ theme }) => ({
  fontSize: '1.5rem',
  fontWeight: 700,
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  textDecoration: 'none',
  [theme.breakpoints.down('md')]: {
    fontSize: '1.25rem',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.1rem',
  },
}));

const LogoEasy = styled('span')(({ theme }) => ({
  color: '#0f172a', // Dark gray/black
}));

const LogoCheck = styled('span')(({ theme }) => ({
  color: '#2563eb', // Blue
}));

const EasyCheckAppBar = ({ onMenuToggle, title = "EasyCheck" }) => {
  const { user, logout } = useEasyCheck();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);

  // Debug log to check user structure
  console.log('🎯 AppBar user object:', user);

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleProfileMenuClose();
    await logout();
  };

  const handleProfileSettings = () => {
    handleProfileMenuClose();
    navigate('/settings');
  };

  const handlePreferences = () => {
    handleProfileMenuClose();
    navigate('/preferences');
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Handle different user object structures
  const getUserName = () => {
    if (!user) return 'User';
    return user.name || user.firstName || user.username || 'User';
  };

  const getUserEmail = () => {
    if (!user) return '';
    return user.email || user.username || '';
  };

  return (
    <MuiAppBar 
      position="static" 
      elevation={0}
      sx={{ 
        backgroundColor: 'white',
        borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
      }}
    >
      <Toolbar sx={{ minHeight: '64px !important' }}>
        {/* Hamburger Menu */}
        <IconButton
          aria-label="toggle menu"
          edge="start"
          onClick={onMenuToggle}
          sx={{ 
            mr: 2,
            color: '#64748b',
            '&:hover': {
              backgroundColor: '#f1f5f9',
            }
          }}
        >
          <MenuIcon />
        </IconButton>

        {/* Spacer to push notifications and user to the right */}
        <Box sx={{ flexGrow: 1 }} />

        {/* Notifications */}
        <IconButton 
          sx={{ 
            mr: 1,
            color: '#666',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.05)',
            }
          }}
        >
          <Badge badgeContent={0} color="error" variant="dot">
            <NotificationsOutlined />
          </Badge>
        </IconButton>

        {/* User Profile Section */}
        {user && (
          <Box>
            <IconButton
              size="large"
              aria-label="user account"
              aria-controls="profile-menu"
              aria-haspopup="true"
              onClick={handleProfileMenuOpen}
              sx={{ 
                p: 0.5,
                ml: 1,
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.05)',
                }
              }}
            >
              <Avatar
                sx={{ 
                  width: 36, 
                  height: 36,
                  bgcolor: '#2563eb',
                  color: 'white',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  border: '2px solid rgba(37, 99, 235, 0.1)'
                }}
              >
                {getInitials(getUserName())}
              </Avatar>
            </IconButton>

            {/* Profile Menu */}
            <Menu
              id="profile-menu"
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleProfileMenuClose}
              onClick={handleProfileMenuClose}
              PaperProps={{
                elevation: 8,
                sx: {
                  overflow: 'visible',
                  filter: 'drop-shadow(0px 4px 12px rgba(0,0,0,0.15))',
                  mt: 1.5,
                  minWidth: 220,
                  borderRadius: 2,
                  '&:before': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                    right: 14,
                    width: 10,
                    height: 10,
                    bgcolor: 'background.paper',
                    transform: 'translateY(-50%) rotate(45deg)',
                    zIndex: 0,
                  },
                }
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              {/* User Info Header */}
              <Box sx={{ px: 3, py: 2, bgcolor: 'primary.main', color: 'white' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar
                    sx={{ 
                      width: 32, 
                      height: 32,
                      bgcolor: 'rgba(255, 255, 255, 0.2)',
                      fontSize: '0.75rem'
                    }}
                  >
                    {getInitials(getUserName())}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {getUserName()}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      {getUserEmail()}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Divider />

              {/* Menu Items */}
              <MenuItem 
                onClick={handleProfileSettings}
                sx={{ py: 1.5, px: 3 }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Person fontSize="small" />
                </ListItemIcon>
                <ListItemText>
                  Profile Settings
                </ListItemText>
              </MenuItem>

              <MenuItem
                onClick={handlePreferences}
                sx={{ py: 1.5, px: 3 }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Settings fontSize="small" />
                </ListItemIcon>
                <ListItemText>
                  Preferences
                </ListItemText>
              </MenuItem>

              <Divider sx={{ my: 1 }} />

              <MenuItem 
                onClick={handleLogout}
                sx={{ 
                  py: 1.5, 
                  px: 3,
                  '&:hover': {
                    backgroundColor: 'error.light',
                    color: 'error.contrastText'
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Logout fontSize="small" color="error" />
                </ListItemIcon>
                <ListItemText>
                  <Typography color="error">
                    Sign Out
                  </Typography>
                </ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        )}
      </Toolbar>
    </MuiAppBar>
  );
};

EasyCheckAppBar.propTypes = {
  onMenuToggle: PropTypes.func.isRequired,
  title: PropTypes.string
};

export default EasyCheckAppBar; 